import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {AccountModel} from '../../../../models/api/accountModel';
import {ContactOrAccountDetailsPanelId} from '../../../../store/panels/panelStateBase';
import {RemoveEmailDomainsContainerDialog} from '../../dialogs/removeEmailDomainsDialogContainer';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';
import {EmailDomainDeleteButtonContainer} from './emailDomainDeleteButtonContainer';
import {EmailDomainEditButtonPopoverCoordinatorContainer} from './emailDomainEditButtonPopoverCoordinatorContainer';

/*
 * Props.
 */

interface EmailDomainsProps {
  account: AccountModel;
  panelId: ContactOrAccountDetailsPanelId;
  shouldDisableActions?: boolean;
}

/*
 * Style.
 */

const StyledEmailDomainsTitleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledEmailDomainsValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

const StyledContentDiv = styled.div`
  margin: 10px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

const StyledValueDiv = styled.div`
  font-size: ${fontSizes.medium};
  position: relative;
  display: flex;
  align-items: center;
  overflow-wrap: anywhere;
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0px;
  top: 4px;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );
  display: flex;
`;

/*
 * Component.
 */

export const EmailDomains: FC<EmailDomainsProps> = (props) => {
  const {account, panelId, shouldDisableActions} = props;
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const [isViewingRemoveDialog, setIsViewingRemoveDialog] = useState(false);
  const {onTrackEvent} = useAccountDetailsContext();

  if (account.domains.length === 0) {
    return null;
  }

  const showControls = () => {
    if (shouldDisableActions) {
      return;
    }
    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  return (
    <>
      <StyledContentDiv>
        <StyledTitleDiv>
          <StyledEmailDomainsTitleDiv>
            <Trans id="account-details-account-information-email-domains">Email domains</Trans>
            <TooltipCoordinator
              render={() => (
                <Trans id="account-details-account-information-automatic-association-tooltip">
                  Contacts with matching email domains will be automatically associated with this Account
                </Trans>
              )}
              delay={0}
              placement="top"
              maxWidth={240}
              customMargin={4}
              persistent
            />
          </StyledEmailDomainsTitleDiv>
        </StyledTitleDiv>
        <StyledValueDiv onMouseEnter={showControls} onMouseMove={showControls} onMouseLeave={hideControls}>
          <StyledEmailDomainsValueDiv>{account.domains.join(', ')}</StyledEmailDomainsValueDiv>
          {shouldShowControls && (
            <StyledControlsDiv>
              <EmailDomainEditButtonPopoverCoordinatorContainer
                panelId={panelId}
                account={account}
                onCancel={hideControls}
              />
              <EmailDomainDeleteButtonContainer
                account={account}
                onClick={() => setIsViewingRemoveDialog(true)}
              />
            </StyledControlsDiv>
          )}
        </StyledValueDiv>
      </StyledContentDiv>
      {isViewingRemoveDialog && (
        <RemoveEmailDomainsContainerDialog
          account={account}
          panelId={panelId}
          onClose={(domainsDeleted) => {
            if (domainsDeleted) {
              onTrackEvent(
                buildAccountDetailsEditPayload(
                  account.id,
                  AccountDetailsEditActionsEnum.DELETE,
                  AccountDetailsEditAttributesEnum.DOMAINS,
                ),
              );
            }
            setIsViewingRemoveDialog(false);
          }}
        />
      )}
    </>
  );
};
